/****************************************************
 * Module Name:  NotificationModalNew.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useRef, useState, useMemo } from "react";
import Constants from "../../utility/constants";
import logo from "../../images/pansuriya.jpg";
import NotificationToAll from "./notificationToAll";
import { Badge, Box, Button, TextField, Tooltip } from "@mui/material";
import { Modal } from "react-bootstrap";
import CommonSnackbar from "../common/commonSnackBar";
import { IoMdAdd } from "react-icons/io";
import { LoadingButton } from "@mui/lab";
import UserProxy from "../../utility/proxy/manager/userProxy";
import Select from "react-select";
import NotificationProxy from "../../utility/proxy/notification/notificationProxy";
import CloseIcon from '@mui/icons-material/Close';
import { ImUser } from "react-icons/im";
import Utility from "../../utility/utility";
import { FaUsers } from "react-icons/fa";
import { BsArrowRightCircleFill } from "react-icons/bs";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

const NotificationModalNew = ({ handleClose, unReadNotification }) => {

    // Permission to send broadcast message
    const broadcastPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.NOTIFICATIONBROADCASTTOALL);
    const [showNotificationToAllModal, setShowNotificationToAllModal] = useState(false);

    // NotificationMap { key: userid, value: { notification: [{chat}, {chat1}, ...]}, statusCount }
    const [notifications, setNotifications] = useState({});
    const [newMessage, setNewMessage] = useState("");           // new message
    const [selectedUserId, setSelectedUserId] = useState();     // selected userid for chat
    const latestSelectedUserId = useRef(selectedUserId);        // To access the latest state value i.e selectedUserId 
    const [selectedChat, setSelectedChat] = useState([]);       // selected userid chat
    const [userLoader, setUserLoader] = useState(false);        // Load User to start new chat

    // New Unread Notification
    const [unreadNotification, setUnreadNotification] = useState(unReadNotification);

    // Boolean - Starting new chat (true/false)
    const [newChatSelection, setNewChatSelection] = useState(false);

    const [timerNumber, setTimerNumber] = useState(null);           // SetInterval - Load latest chat
    const [usersList, setUsersList] = useState();               // Whole Response (New Chat UserDetails)
    const [userOptions, setUserOptions] = useState([]);           // {value: id, label: username} - (New Chat UserDetails)
    const [allowSendMessage, setAllowSendMessage] = useState(); // Boolean - Can send message (true/false)
    const [hoveredUser, setHoveredUser] = useState(null);       // CSS
    const [users, setUsers] = useState();                       // Existing Chat UserDetails

    // Create a reference to access the chat container DOM element
    const chatContainerRef = useRef(null);
    const [initialLoad, setInitialLoad] = useState(false);
    const [oldNotificationLoader, setOldNotificationLoader] = useState(false);
    const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);

    // Snackbar
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [userInputValue, setUserInputValue] = useState("");
    const [debouncedUserInputValue, setDebouncedUserInputValue] = useState("");

    // Set Snackbar
    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    useEffect(() => {
        // When the selectedChat changes (new chat is selected or messages updated)
        // Scroll the container to the bottom
        setShowLoadMoreButton(true);
        if (chatContainerRef.current)
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

        if (latestSelectedUserId.current) {
            if (timerNumber) {
                clearInterval(timerNumber);
            }
            setTimerNumber(setInterval(reloadBetweenUserChatAndUnreadCount, 15000));
        }

    }, [selectedChat, initialLoad]); // Run this effect every time the chat messages change

    // Update the ref whenever state changes
    useEffect(() => {
        latestSelectedUserId.current = selectedUserId;
    }, [selectedUserId]);

    useEffect(() => {
        // Fetch All Notification for logged-in user
        NotificationProxy.GetNotificationsForUsersByLimit(Constants.loggedInUser.id, false, Constants.NOTIFICATION_LIMIT, (response) => {
            if (response) {
                handleNotifications(response);
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
            }
        })
    }, []);

    // Process the raw data into a structured format
    const handleNotifications = (notifications) => {
        if (notifications.length === 0) return;

        let notificationMap = {}; // Stores notifications categorized by user ID
        let selectedUserId = "";

        notifications.forEach(notification => {
            let otherUserId = (notification.fromid === Constants.loggedInUser.id)
                ? notification.toid
                : notification.fromid;

            // Initialize notification entry if not present
            if (!notificationMap[otherUserId]) {
                notificationMap[otherUserId] = { notifications: [], pagination: {} };
            }

            // Add notification to the user's list
            notificationMap[otherUserId].notifications.unshift(notification);

            // Set offset based on the number of records exist - max will be 20 as limit is 20
            notificationMap[otherUserId].pagination["offset"] = notificationMap[otherUserId].notifications.length;

            // Determine message status
            let messageStatus = (notification.status === "DELIVERED" &&
                notification.fromid !== Constants.loggedInUser.id) ? "UNREAD" : notification.status;

            // Update notification count for the status
            notificationMap[otherUserId][messageStatus] = (notificationMap[otherUserId][messageStatus] || 0) + 1;
        });

        if (notifications.length > 0) {
            selectedUserId = Object.keys(notificationMap)[0];
        }

        // Update state with notifications and selected user
        setNotifications(notificationMap);

        setSelectedUserId(selectedUserId);
        setSelectedChat(notificationMap[selectedUserId] ? notificationMap[selectedUserId].notifications : []);

        // Fetch additional user details
        UserProxy.GetMultipleUsers(
            { ids: Object.keys(notificationMap).map(id => parseInt(id)) },
            (userList) => {
                let selectedUser = userList.find(user => parseInt(user.id) === parseInt(selectedUserId));
                setAllowSendMessage(selectedUser ? !Constants.RESTRICTED_NOTIFICATION_USERS
                    .includes(selectedUser.username.toLowerCase()) : true);
                setUsers(userList);
                setInitialLoad(true);
            }
        );
    };

    // Close Notification To All Modal
    const handleCloseNotificationToAll = (showSnackbar) => {
        setShowNotificationToAllModal(false);
        setSnackbar(showSnackbar, "Notification is sent to all the users....", "success");
    }

    // Load all users for new chat
    const getAllUsers = () => {
        // List of all users with existing chats
        let data = { ids: Object.keys(notifications).map(userId => parseInt(userId)) };

        // Retrieve a list of all users excluding those who already have existing chats
        setUserLoader(true);
        UserProxy.GetAllUserExceptMini(data, response => {
            if (response) {
                setAllUsers(response);
                setUserLoader(false);
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setUserLoader(false);
            }
        })
    }

    const setAllUsers = (response) => {
        let optionArray = response.map(iUser => ({
            value: iUser.id,
            label: iUser.username
        }));

        setNewChatSelection(true);
        setUserOptions(optionArray);
        setUsersList(response);
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedUserInputValue(userInputValue);
        }, 500);

        return () => clearTimeout(handler);
    }, [userInputValue]);

    const filteredUserOptions = useMemo(() => {
        if (!debouncedUserInputValue) {
            return userOptions.slice(0, 100);
        }
        return userOptions.filter(option =>
            option.label.toLowerCase().includes(debouncedUserInputValue.toLowerCase())
        ).slice(0, 500);
    }, [debouncedUserInputValue, userOptions]);

    const reloadBetweenUserChatAndUnreadCount = () => {
        NotificationProxy.GetLatestNotificationsBetweenUsers(
            selectedChat.length === 0 ? null : selectedChat[selectedChat.length - 1].id,
            Constants.loggedInUser.id,
            latestSelectedUserId.current,
            false,
            (response) => updateChatWithLatestNotification(response)
        );
        NotificationProxy.GetUnreadNotificationForUserCount(
            Constants.loggedInUser.id,
            false,
            (response) => setUnreadNotification(response)
        );
    }

    const updateChatWithLatestNotification = (latestNotifications) => {

        if (!latestNotifications || latestNotifications.length === 0) return;

        const container = chatContainerRef.current;

        // Check if user is already near the bottom before updating
        const isUserNearBottom = (container.scrollHeight - container.scrollTop - container.clientHeight) < 100;

        let notificationMap = notifications; // Copy current notifications

        // Determine the user ID to update chat for
        let chatUserId = (latestNotifications[0].fromid === Constants.loggedInUser.id) ?
            latestNotifications[0].toid : latestNotifications[0].fromid;

        // Add the latest notifications in the end
        latestNotifications.forEach(iNotification => {
            notificationMap[chatUserId].notifications.push(iNotification);

            // Determine message status
            let messageStatus = (iNotification.status === "DELIVERED" &&
                iNotification.fromid !== Constants.loggedInUser.id) ? "UNREAD" : iNotification.status;

            // Update notification count for the status
            notificationMap[chatUserId][messageStatus] = (notificationMap[chatUserId][messageStatus] || 0) + 1;
        });

        // Update the offset value
        notificationMap[chatUserId].pagination.offset = notificationMap[chatUserId].pagination.offset + latestNotifications.length;

        //Mark notification as read
        markNotificationAsRead(chatUserId, notificationMap.notifications);

        setNotifications(notificationMap);
        setSelectedChat(notificationMap[chatUserId].notifications);

        // Scroll only if user was already at the bottom
        if (isUserNearBottom) {
            setTimeout(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                }
            }, 10); // delay helps after DOM updates
        }

    }

    const handleSelectChat = (userId) => {
        // Find the user based on ID
        const user = users.find((u) => parseInt(u.id) === parseInt(userId));

        // Check if user is restricted from receiving messages
        const isRestricted = user
            ? Constants.RESTRICTED_NOTIFICATION_USERS.includes(user.username.toLowerCase())
            : false;

        setAllowSendMessage(!isRestricted);

        // Get chat messages from notifications
        const userNotifications = notifications[userId] ? notifications[userId].notifications : [];

        setSelectedUserId(userId);
        setSelectedChat(userNotifications);

        // Mark notifications as read
        markNotificationAsRead(userId, userNotifications);
    };

    const markNotificationAsRead = (userId, readNotifications) => {
        let notificationIdsToUpdate = readNotifications.filter(notification =>
            notification.toid === Constants.loggedInUser.id && notification.status === "DELIVERED")
            .map(notification => notification.id);

        if (notificationIdsToUpdate.length > 0) {
            NotificationProxy.UpdateMultipleNotificationStatus(
                { id: notificationIdsToUpdate, status: "READ" },
                (response) => {
                    if (response)
                        updateStateStatus(userId, notificationIdsToUpdate)
                }
            );
        }
    };

    const handleSendMessage = () => {
        if (!newMessage || newMessage.trim() === "") return;

        const messageData = {
            fromid: Constants.loggedInUser.id,
            toid: selectedUserId,
            messagetime: Utility.GetUTCTime(),
            message: newMessage,
            status: "NEW",
            deleted: false,
        };

        NotificationProxy.AddNotification(messageData, (response) => {
            const updatedNotifications = notifications;

            if (!updatedNotifications[selectedUserId]) {
                updatedNotifications[selectedUserId] = { notifications: [], NEW: 0 };
            }

            // Add new message to the chat
            updatedNotifications[selectedUserId].notifications.push(response);
            updatedNotifications[selectedUserId].pagination.offset++;

            // Update the message status counter
            const status = response.status;
            if (!updatedNotifications[selectedUserId][status]) {
                updatedNotifications[selectedUserId][status] = 1;
            } else {
                updatedNotifications[selectedUserId][status]++;
            }

            setNotifications(updatedNotifications);
            setSelectedChat([...updatedNotifications[selectedUserId].notifications]);
            setNewMessage("");
        });
    };

    // Load Old Notifications
    const loadOldNotifications = () => {

        setOldNotificationLoader(true);

        // Save current scroll position
        const prevScrollHeight = chatContainerRef.current.scrollHeight;
        const prevScrollTop = chatContainerRef.current.scrollTop;

        NotificationProxy.GetNotificationsBetweenUsersByLimitOffset(
            Constants.loggedInUser.id,
            selectedUserId,
            false,
            notifications[selectedUserId].pagination.offset,
            Constants.NOTIFICATION_LIMIT,
            (oldNotifications) => {
                if (oldNotifications) {

                    if (oldNotifications.length === 0) {
                        setShowLoadMoreButton(false);
                        setOldNotificationLoader(false);
                        return
                    };

                    let notificationMap = notifications; // Copy current notifications

                    // Determine the user ID to update chat for
                    let chatUserId = (oldNotifications[0].fromid === Constants.loggedInUser.id) ?
                        oldNotifications[0].toid : oldNotifications[0].fromid;

                    // Add the old notifications in the start
                    oldNotifications.forEach(iNotification => {
                        notificationMap[chatUserId].notifications.unshift(iNotification);
                    });

                    // Update the offset value
                    notificationMap[chatUserId].pagination.offset = notificationMap[chatUserId].pagination.offset + oldNotifications.length;

                    setNotifications(notificationMap);
                    setSelectedChat(notificationMap[chatUserId].notifications);

                    // Wait for DOM to render new messages, then restore scroll position
                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            const newScrollHeight = chatContainerRef.current.scrollHeight;
                            chatContainerRef.current.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
                        }
                    }, 10); // 0 or 50ms usually works fine

                    // Mark notifications as read
                    markNotificationAsRead(chatUserId, notificationMap[chatUserId].notifications);
                } else {
                    setShowSnackbar(true, Constants.APiFailErrorMessage, "error");
                }
                setOldNotificationLoader(false);
            }
        )
    }

    // When messages are marked READ, this updates their status in local state.
    const updateStateStatus = (userId, idsToUpdate) => {
        let notificationMap = notifications;

        notificationMap[userId].notifications.forEach(notification => {
            if (idsToUpdate.includes(parseInt(notification.id))) {
                if (notificationMap[userId][notification.status] > 0) {
                    notificationMap[userId][notification.status]--;
                }
                notification.status = "READ";
                notificationMap[userId]["READ"] = (notificationMap[userId]["READ"] || 0) + 1;
            }
        });

        setNotifications(notificationMap);
    };

    // Returns username for a given user ID (adds "Self" if it’s the current user).
    const getUserName = (userid) => {
        if (users === undefined || users.length === 0) return;

        let user = users.find((iUser) => parseInt(iUser.id) === parseInt(userid));
        return (
            (user ? user.username : userid) +
            (parseInt(userid) === parseInt(Constants.loggedInUser.id) ? " (Self)" : "")
        );
    };

    // Displays unread count badge for a given user.
    const getUnreadCount = (key) => {
        let unreadCount = unreadNotification.find((iCnt) => parseInt(iCnt.id) === parseInt(key));

        if (unreadCount !== undefined && unreadCount !== null)
            return (
                <Box
                    sx={{
                        "& .MuiBadge-badge": { color: "white", background: "#1DAB61" }
                    }}
                >
                    <Badge badgeContent={unreadCount.count} max={99}></Badge>
                </Box>
            );

        return <Box />;
    };

    const closeModal = () => {
        if (timerNumber !== 0)
            clearInterval(timerNumber);

        handleClose();
    }

    return (
        <Box>

            {showNotificationToAllModal ? (
                <NotificationToAll
                    handleClose={handleCloseNotificationToAll}
                />
            ) : null}

            <Modal
                show={true}
                onHide={closeModal}
                enforceFocus={true}
                backdrop="static"
                size="xl"
            >

                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Notifications
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <table style={{ width: "100%" }}>
                        <tr>
                            {/* Left Side */}
                            <td
                                style={{
                                    width: "30%",
                                    borderRight: "1px solid lightgrey"
                                }}
                            >
                                <Box>
                                    <table style={{ width: "100%" }}>
                                        <tr>
                                            <td>
                                                {newChatSelection ? (
                                                    <Box display={"flex"} alignItems={"center"} gap={"8px"}>
                                                        <Select
                                                            menuPlacement="auto"
                                                            blurInputOnSelect={false}
                                                            value={""}
                                                            isSearchable={true}
                                                            name="Users"
                                                            menuPortalTarget={document.body}
                                                            options={filteredUserOptions}
                                                            styles={{
                                                                ...Constants.Styles,
                                                                control: (base) => ({
                                                                    ...base,
                                                                    width: '11rem', // Adjust the width as needed
                                                                    minHeight: "36px",
                                                                    borderRadius: "3px"
                                                                }),
                                                            }}
                                                            inputValue={userInputValue}
                                                            onInputChange={(inputValue) => setUserInputValue(inputValue)}
                                                            onChange={(selectedOption) => {
                                                                // Create a copy of the existing notifications to avoid direct mutation
                                                                setNotifications(prevNotifications => ({
                                                                    ...prevNotifications,
                                                                    [selectedOption.value]: { notifications: [], pagination: { offset: 0 } },
                                                                }));

                                                                // Create copies of users and remaining users
                                                                let updatedUsers = [...users];
                                                                let updatedRemainingUsers = [...usersList];

                                                                // Find the selected user from the remaining users list
                                                                let selectedUserIndex = updatedRemainingUsers.findIndex(
                                                                    user => parseInt(user.id) === parseInt(selectedOption.value)
                                                                );

                                                                if (selectedUserIndex >= 0) {
                                                                    // Add the selected user to the users list
                                                                    updatedUsers.push(updatedRemainingUsers[selectedUserIndex]);

                                                                    // Remove the selected user from the remaining users list
                                                                    updatedRemainingUsers.splice(selectedUserIndex, 1);
                                                                }

                                                                // Update state using hooks
                                                                setNewChatSelection(false);
                                                                setUsers(updatedUsers);
                                                                setUsersList(updatedRemainingUsers);
                                                                setSelectedChat([]);  // Open the selected chat
                                                                setSelectedUserId(selectedOption.value);
                                                            }}
                                                        />
                                                        <CloseIcon sx={{
                                                            fontSize: "24px",
                                                            color: "red",
                                                            border: "2px solid red",
                                                            borderRadius: "3px",
                                                            cursor: "pointer"
                                                        }} onClick={() => setNewChatSelection(false)} />
                                                    </Box>
                                                ) : (
                                                    <LoadingButton
                                                        variant="outlined"
                                                        size="small"
                                                        color='inherit'
                                                        onClick={() => getAllUsers()}
                                                        startIcon={<IoMdAdd color="inherit" size={20} />}
                                                        loadingPosition="start"
                                                        loading={userLoader}
                                                        sx={{ width: "93%", height: "36px", borderRadius: "3px" }}
                                                    >
                                                        New Chat
                                                    </LoadingButton>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Box style={{ height: Utility.isAddAllowed(broadcastPermission) || Utility.isUpdateAllowed(broadcastPermission) ? "510px" : "550px", overflowX: "hidden" }}>
                                                    {Object.keys(notifications).map((key, index) => {
                                                        const userName = getUserName(key);
                                                        const isLoggedInUser = parseInt(key) === Constants.loggedInUser.id;
                                                        const isSelected = parseInt(selectedUserId) === parseInt(key);
                                                        const isRestrictedUser = Constants.RESTRICTED_NOTIFICATION_USERS.includes(userName);
                                                        const isSupportUser = Constants.SUPPORT_USERS.includes(userName);

                                                        // Determine background color based on user type
                                                        const backgroundColor = isRestrictedUser
                                                            ? "#F8CBAD"
                                                            : isSupportUser
                                                                ? "#C6E0B4"
                                                                : isLoggedInUser
                                                                    ? "#BDD7EE"
                                                                    : isSelected
                                                                        ? "#D6DCE4"
                                                                        : "white";

                                                        const hoverColor = backgroundColor === "white" ? "#F5F5F5" : backgroundColor;

                                                        return (
                                                            <Box
                                                                key={index}
                                                                onClick={() => handleSelectChat(key)}
                                                                onMouseEnter={() => setHoveredUser(key)}
                                                                onMouseLeave={() => setHoveredUser(null)}
                                                                style={{
                                                                    position: "relative",
                                                                    backgroundColor: hoveredUser === key ? hoverColor : backgroundColor,
                                                                    cursor: "pointer",
                                                                    margin: "5px 8px 5px 0",
                                                                    borderRadius: "3px",
                                                                    transition: "background-color 0.2s ease-in-out", // Smooth hover effect
                                                                }}
                                                            >
                                                                <Box
                                                                    style={{
                                                                        padding: "5pt",
                                                                        width: "120px",
                                                                        display: "flex",
                                                                        flexWrap: "nowrap",
                                                                        minWidth: "100%",
                                                                        // justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        gap: "5px"
                                                                    }}
                                                                >
                                                                    <ImUser />
                                                                    &nbsp;{userName}
                                                                    {/* {getUnreadCount(key)} */}
                                                                </Box>
                                                                <Box
                                                                    style={{
                                                                        textAlign: "right",
                                                                        fontSize: "10.5px",
                                                                        position: "absolute",
                                                                        right: "20px",
                                                                        top: "10px",
                                                                    }}
                                                                >
                                                                    {getUnreadCount(key)}
                                                                </Box>
                                                            </Box>
                                                        )
                                                    })}
                                                </Box>
                                            </td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td>
                                                {Utility.isAddAllowed(broadcastPermission) || Utility.isUpdateAllowed(broadcastPermission) ? (
                                                    <Box width={"93%"} paddingTop={"10px"} borderTop={"1px solid #f0f0f0"}>
                                                        <Tooltip title="Send Notification to All">
                                                            <LoadingButton
                                                                variant="outlined"
                                                                size="small"
                                                                color='primary'
                                                                onClick={() => setShowNotificationToAllModal(true)}
                                                                startIcon={<FaUsers color="primary" size={20} />}
                                                                loadingPosition="start"
                                                                sx={{ width: "100%", height: "36px", borderRadius: "3px" }}
                                                            >
                                                                Message to all
                                                            </LoadingButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : null
                                                }
                                            </td>
                                        </tr>
                                    </table>
                                </Box>
                            </td>

                            {/* Right Side */}
                            <td>

                                {/* Username */}
                                <Box sx={{
                                    marginLeft: "15px",
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                    borderBottom: "1px solid #f0f0f0",
                                    marginBottom: "5px",
                                    fontWeight: 700,
                                    fontSize: "22px"
                                }}>
                                    {getUserName(selectedUserId)}
                                </Box>

                                {/* Chat Screen */}
                                <Box
                                    ref={chatContainerRef}
                                    style={{
                                        height: allowSendMessage ? "505px" : "545px",
                                        overflowX: "hidden",
                                        marginLeft: "15px",
                                        paddingRight: "5px",
                                        width: "54.4rem"
                                    }}
                                >
                                    <table style={{ width: "100%" }}>
                                        <tbody>
                                            <Box textAlign={"center"} marginBottom={"5px"}>
                                                <LoadingButton
                                                    variant="contained"
                                                    loading={oldNotificationLoader}
                                                    loadingPosition="start"
                                                    startIcon={showLoadMoreButton ? <RefreshOutlinedIcon /> : ""}
                                                    onClick={() => loadOldNotifications()}
                                                    color={"primary"}
                                                    sx={{
                                                        height: "30px",
                                                        fontSize: "12px",
                                                    }}
                                                    disabled={!showLoadMoreButton}
                                                >
                                                    {showLoadMoreButton ? "Load Old Message" : "No more messages"}
                                                </LoadingButton>
                                            </Box>
                                            {selectedChat.map((iChat) => {
                                                return (
                                                    <tr key={iChat.id}>
                                                        <td>
                                                            <Box
                                                                style={{
                                                                    backgroundColor: iChat.fromid === Constants.loggedInUser.id ? "#E2FFC9" : "#DEF2FF",
                                                                    float: iChat.fromid === Constants.loggedInUser.id ? "right" : "left",
                                                                    padding: "5pt",
                                                                    margin: "5px 0",
                                                                    borderRadius: "3px",
                                                                    boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                                                    maxWidth: allowSendMessage ? "85%" : "100%"
                                                                }}
                                                            >
                                                                {iChat.message}
                                                                <Box
                                                                    style={{
                                                                        fontSize: "6pt",
                                                                        textAlign: iChat.fromid === Constants.loggedInUser.id ? "right" : "left",
                                                                        verticalAlign: "bottom",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {Utility.ConvertUtcToLocalTime(iChat.messagetime)}
                                                                </Box>
                                                            </Box>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Send Message */}
                                {allowSendMessage ? (
                                    <Box
                                        marginLeft={"15px"}
                                        display={"flex"}
                                        alignItems={"center"}
                                        gap={"5px"}
                                        paddingTop={"10px"}
                                        borderTop={"1px solid #f0f0f0"}
                                    >
                                        <Box sx={{ "& .MuiFormControl-root": { margin: 0 } }}>
                                            <TextField
                                                margin="normal"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        padding: "7px 14px !important"
                                                    },
                                                    width: "50rem",
                                                }}
                                                multiline
                                                rows={1}
                                                placeholder={`Send Message to ${getUserName(selectedUserId)}`}
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter")
                                                        handleSendMessage();
                                                }}
                                            />
                                        </Box>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSendMessage()}
                                            disabled={!newMessage}
                                        >
                                            <BsArrowRightCircleFill size="25" />
                                        </Button>
                                    </Box>
                                ) : null}
                            </td>
                        </tr>


                    </table>

                </Modal.Body>

            </Modal>

        </Box >
    )
}

export default NotificationModalNew;